import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { Observable, ReplaySubject } from 'rxjs';

import { WINDOW } from '../browser/window/window.token';

// For more information about detecting prerendering/speculated pages, see:
// https://developer.mozilla.org/en-US/docs/Web/API/Speculation_Rules_API#javascript_prerender_detection.

@Injectable({
    providedIn: 'root',
})
export class SpeculationDetectionService {
    private document = inject(DOCUMENT);
    readonly #window = inject(WINDOW);

    private _prerenderActivate$ = new ReplaySubject<void>();

    constructor() {
        if (this.isPrerendering()) {
            this.document.addEventListener('prerenderingchange', () => this.onPrerenderingChange(), { once: true });
        }
    }

    /** Emits a notification when the page is activated, if it was prerendered */
    get prerenderActivate$(): Observable<void> {
        return this._prerenderActivate$;
    }

    /**
     * Returns true if the current page is a not-yet-activated prerendered page - and is still an invisible, background tab created by another page.
     */
    isPrerendering(): boolean {
        const document = this.document as Document & { prerendering: boolean | undefined };

        return Boolean(document.prerendering);
    }

    /**
     * True if the page is an activated prerendered page - e.g. it has been brought to the foreground by a link being clicked.
     */
    isPrerendered(): boolean {
        const initialNavigationPerfEntry = this.#window.performance?.getEntriesByType?.('navigation')[0] as
            | (PerformanceEntry & { activationStart: number | undefined })
            | undefined;

        const prerenderActivationTime = initialNavigationPerfEntry?.activationStart;

        return Boolean(prerenderActivationTime && prerenderActivationTime > 0);
    }

    private onPrerenderingChange() {
        this._prerenderActivate$.next();
        this._prerenderActivate$.complete();
    }
}
